import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { exportToPDF } from '../utils/exportUtils';
import './CustomReports.css';

const CustomReports = ({ onClose, addToast, confirmAction }) => {
  const { authFetch, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    start: '',
    end: '',
    type: 'all', // all, incomes, expenses
    status: 'all', // all, pago, pendente
    category: '',
    tags: '',
    member: '', // for business vision
  });
  const [categories, setCategories] = useState([]);
  const [members, setMembers] = useState([]);
  const [vision, setCurrentVision] = useState('personal');

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      // Sync vision from localStorage or use personal as default
      const v = localStorage.getItem('wf_vision') || 'personal';
      setCurrentVision(v);

      const resCat = await authFetch('/api/categories');
      if (resCat.ok) setCategories(await resCat.json());

      const visionMode = v.startsWith('business') ? 'business' : 'personal';
      const visionAccountId = v.startsWith('business:') ? v.split(':')[1] : null;

      if (visionMode === 'business') {
        const membersUrl = visionAccountId
          ? `/api/business/members?account_id=${visionAccountId}`
          : '/api/business/members';
        const resMem = await authFetch(membersUrl);
        if (resMem.ok) setMembers(await resMem.json());
      }
    } catch (err) {
      console.warn('Erro ao carregar dados iniciais do relatório:', err);
    }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      // Re-use logic from /api/reports with exact filters
      const q = new URLSearchParams();
      if (filters.start) q.append('start', filters.start.replace(/-/g, ''));
      if (filters.end) q.append('end', filters.end.replace(/-/g, ''));
      
      const visionMode = vision.startsWith('business') ? 'business' : 'personal';
      const visionAccountId = vision.startsWith('business:') ? vision.split(':')[1] : null;

      q.append('vision', visionMode);
      if (visionMode === 'business' && visionAccountId) {
          q.append('account_id', visionAccountId);
      }

      if (filters.category) q.append('category', filters.category);
      if (filters.status)   q.append('status', filters.status);
      if (visionMode === 'business' && filters.member) q.append('member', filters.member);

      const res = await authFetch(`/api/reports?${q.toString()}`);
      if (res.ok) {
        let report = await res.json();
        const txs = report.transactions;
        
        setData({
          transactions: txs,
          balances: report.balances,
          period: report.period,
          accountName: report.accountName || null,
        });
      }
    } catch (err) {
      addToast('Erro ao gerar relatório', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    if (!data || data.transactions.length === 0) {
      addToast('Gere o relatório primeiro com resultados válidos.', 'warning');
      return;
    }
    const period = { 
      start: data.period?.start || filters.start || 'Sempre', 
      end: data.period?.end || filters.end || 'Sempre' 
    };
    // Resolve o nome correto da conta para o PDF
    const visionMode = vision.startsWith('business') ? 'business' : 'personal';
    const visionAccountId = vision.startsWith('business:') ? vision.split(':')[1] : null;
    const activeAccountName = (visionMode === 'business' && data.accountName)
      ? data.accountName
      : user?.name || 'Pessoal';
    
    exportToPDF(data.transactions, data.balances, period, { 
        ...user, 
        name: activeAccountName,
        accountType: visionMode === 'business' ? 'Conta Business' : 'Pessoal' 
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="cr-modal" onClick={e => e.stopPropagation()}>
        <div className="cr-header">
          <div>
            <h3>📊 Relatórios Customizados</h3>
            <p>Gere extratos específicos cruzando filtros avançados.</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="cr-form">
          <div className="cr-row">
            <div className="cr-field">
              <label>Data Inicial</label>
              <input type="date" value={filters.start} onChange={e=>setFilters({...filters, start: e.target.value})} />
            </div>
            <div className="cr-field">
              <label>Data Final</label>
              <input type="date" value={filters.end} onChange={e=>setFilters({...filters, end: e.target.value})} />
            </div>
          </div>
          
          <div className="cr-row">
            <div className="cr-field">
              <label>Tipo</label>
              <select value={filters.type} onChange={e=>setFilters({...filters, type: e.target.value})}>
                <option value="all">Todas</option>
                <option value="incomes">Apenas Receitas</option>
                <option value="expenses">Apenas Despesas</option>
              </select>
            </div>
            <div className="cr-field">
              <label>Status</label>
              <select value={filters.status} onChange={e=>setFilters({...filters, status: e.target.value})}>
                <option value="all">Todos</option>
                <option value="pago">Efetivado / Pago</option>
                <option value="pendente">Pendente</option>
              </select>
            </div>
          </div>

          <div className="cr-row">
            <div className="cr-field">
              <label>Categoria (Opcional)</label>
              <select value={filters.category} onChange={e=>setFilters({...filters, category: e.target.value})}>
                <option value="">Todas</option>
                {categories.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
              </select>
            </div>
            {vision === 'business' ? (
              <div className="cr-field">
                <label>Membro (Opcional)</label>
                <select value={filters.member} onChange={e=>setFilters({...filters, member: e.target.value})}>
                  <option value="">Todos os Membros</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
                </select>
              </div>
            ) : (
              <div className="cr-field">
                <label>Tag (Opcional)</label>
                <input type="text" placeholder="Ex: lazer, escola, viagem..." value={filters.tags} onChange={e=>setFilters({...filters, tags: e.target.value})} />
              </div>
            )}
          </div>

          <div className="cr-actions">
            <button className="cr-btn-primary" onClick={generateReport} disabled={loading}>
              {loading ? 'Gerando...' : 'Gerar Relatório'}
            </button>
            {data && data.transactions.length > 0 && (
              <button className="cr-btn-export" onClick={handleExportPDF}>
                📄 Exportar PDF
              </button>
            )}
          </div>
        </div>

      {data && (
        <div className="cr-results">
          <div className="cr-summary-grid">
            <div className="cr-summary-card">
              <label>Saldo Anterior</label>
              <div className="val">R$ {data.balances.previous.toLocaleString('pt-BR', {minimumFractionDigits:2})}</div>
            </div>
            <div className="cr-summary-card">
              <label>Recebido</label>
              <div className="val income">R$ {data.balances.received.toLocaleString('pt-BR', {minimumFractionDigits:2})}</div>
            </div>
            <div className="cr-summary-card">
              <label>A Receber</label>
              <div className="val info">R$ {data.balances.toReceive.toLocaleString('pt-BR', {minimumFractionDigits:2})}</div>
            </div>
            <div className="cr-summary-card">
              <label>Despesas</label>
              <div className="val expense">R$ {data.balances.expenses.toLocaleString('pt-BR', {minimumFractionDigits:2})}</div>
            </div>
            <div className="cr-summary-card">
              <label>A Pagar</label>
              <div className="val warning">R$ {data.balances.toPay.toLocaleString('pt-BR', {minimumFractionDigits:2})}</div>
            </div>
            <div className="cr-summary-card">
              <label>Saldo Previsto</label>
              <div className="val projected">R$ {data.balances.projected.toLocaleString('pt-BR', {minimumFractionDigits:2})}</div>
            </div>
            <div className="cr-summary-card highlight">
              <label>Saldo Real</label>
              <div className={`val ${data.balances.actual >= 0 ? 'income' : 'expense'}`}>
                R$ {data.balances.actual.toLocaleString('pt-BR', {minimumFractionDigits:2})}
              </div>
            </div>
          </div>
          
          <table className="cr-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Descrição</th>
                <th>Categoria</th>
                <th>Tipo</th>
                <th style={{textAlign:'right'}}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {data.transactions.length === 0 ? (
                <tr><td colSpan="5" style={{textAlign:'center'}}>Nenhum resultado para estes filtros.</td></tr>
              ) : (
                data.transactions.map(t => (
                  <tr key={t.id}>
                    <td>{t.data ? new Date(t.data + 'T12:00:00').toLocaleDateString('pt-BR') : t.date}</td>
                    <td>{t.descricao || t.description || t.descrição}</td>
                    <td>{t.category || t.categoria}</td>
                    <td>
                      <span className={`badge ${['receita','entrada'].includes(t.type||'') ? 'success' : 'danger'}`}>
                        {t.type === 'receita' || t.type === 'entrada' ? '📈 Receita' : '📉 Despesa'}
                      </span>
                    </td>
                    <td style={{textAlign:'right', fontWeight: 600}}>
                      R$ {parseFloat(t.value || t.valor).toFixed(2).replace('.', ',')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
};

export default CustomReports;
